import { useState, type DragEvent } from "react";
import type { RequestDto } from "../lib/api";
import { RequestCard } from "./RequestCard";

interface Props {
  title: string;
  section: "paid" | "free" | "completed" | "skipped";
  items: RequestDto[];
  emptyText: string;
  busyId?: string | null;
  onComplete?: (r: RequestDto) => void;
  onSkip?: (r: RequestDto) => void;
  /** Permanently delete the request. Present only for active sections. */
  onRemove?: (r: RequestDto) => void;
  onTogglePin?: (r: RequestDto) => void;
  /** Override the matched amount (cents). Present only for reorderable sections. */
  onEditAmount?: (r: RequestDto, amountCents: number) => void;
  /** Set/clear the request's freeform note. Present only for reorderable sections. */
  onEditNotes?: (r: RequestDto, notes: string) => void;
  /** Correct the request's video link. Present only for reorderable sections. */
  onEditLink?: (r: RequestDto, youtubeUrl: string) => void;
  /** Return a completed/skipped item to the queue. Present only for those sections. */
  onReactivate?: (r: RequestDto) => void;
  /** Reorder `r` to a computed gap-based position. Absent = section is fixed. */
  onMove?: (r: RequestDto, position: number) => void;
  note?: string;
  /** Render the section as a collapsible group, hidden by default. */
  collapsible?: boolean;
}

/** Sentinel `dropBeforeId` meaning "drop at the very end of the list". */
const END = " end";

/**
 * Gap-based position (100, 200, 300…) that lands the dragged item just above
 * `beforeId`, or at the tail when `beforeId` is END. The API pins whatever it
 * moves and resequences when a gap runs out, so a degenerate midpoint is fine.
 */
function positionForDrop(
  items: RequestDto[],
  dragId: string,
  beforeId: string,
): number | null {
  const without = items.filter((x) => x.id !== dragId);
  const idx =
    beforeId === END ? without.length : without.findIndex((x) => x.id === beforeId);
  if (idx < 0) return null;

  const before = without[idx - 1]?.position ?? 0;
  const after = without[idx]?.position ?? before + 200;
  const mid = Math.floor((before + after) / 2);
  if (mid <= before || mid >= after) return before + 1; // no room; let the API resequence
  return mid;
}

export function QueueSection({
  title,
  section,
  items,
  emptyText,
  busyId,
  onComplete,
  onSkip,
  onRemove,
  onTogglePin,
  onEditAmount,
  onEditNotes,
  onEditLink,
  onReactivate,
  onMove,
  note,
  collapsible,
}: Props) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropBeforeId, setDropBeforeId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(true);
  const reorderable = !!onMove;
  const open = !collapsible || !collapsed;

  function clearDrag() {
    setDragId(null);
    setDropBeforeId(null);
  }

  /** Insert above `r` or below it, depending on which half the pointer is over. */
  function onCardDragOver(e: DragEvent, r: RequestDto, index: number) {
    if (!dragId) return;
    e.preventDefault(); // allow the drop
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const below = e.clientY - rect.top > rect.height / 2;
    const target = below ? (items[index + 1]?.id ?? END) : r.id;
    // Hovering either side of the dragged card itself is a no-op.
    if (target === dragId) return;
    setDropBeforeId(target);
  }

  function onCardDrop(e: DragEvent) {
    e.preventDefault();
    if (dragId && dropBeforeId) {
      const dragged = items.find((x) => x.id === dragId);
      const position = dragged && positionForDrop(items, dragId, dropBeforeId);
      if (dragged && position != null) onMove?.(dragged, position);
    }
    clearDrag();
  }

  return (
    <section className={`section ${section}`}>
      {collapsible ? (
        <button
          type="button"
          className={`section-head section-toggle ${open ? "open" : ""}`}
          aria-expanded={open}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className="chevron" aria-hidden="true">
            ▾
          </span>
          <h2>{title}</h2>
          <span className="count">{items.length}</span>
          {note && <span className="count">· {note}</span>}
        </button>
      ) : (
        <div className="section-head">
          <h2>{title}</h2>
          <span className="count">{items.length}</span>
          {note && <span className="count">· {note}</span>}
        </div>
      )}

      {!open ? null : items.length === 0 ? (
        <div className="empty">{emptyText}</div>
      ) : (
        items.map((r, i) => (
          <RequestCard
            key={r.id}
            request={r}
            busy={busyId === r.id}
            draggable={reorderable}
            dragging={dragId === r.id}
            dropBefore={dropBeforeId === r.id}
            dropAfter={dropBeforeId === END && i === items.length - 1}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", r.id); // Firefox needs a payload
              setDragId(r.id);
            }}
            onDragOver={(e) => onCardDragOver(e, r, i)}
            onDrop={onCardDrop}
            onDragEnd={clearDrag}
            onComplete={() => onComplete?.(r)}
            onSkip={() => onSkip?.(r)}
            onRemove={onRemove && (() => onRemove(r))}
            onTogglePin={onTogglePin && (() => onTogglePin(r))}
            onEditAmount={onEditAmount && ((cents) => onEditAmount(r, cents))}
            onEditNotes={onEditNotes && ((notes) => onEditNotes(r, notes))}
            onEditLink={onEditLink && ((youtubeUrl) => onEditLink(r, youtubeUrl))}
            onReactivate={onReactivate && (() => onReactivate(r))}
          />
        ))
      )}
    </section>
  );
}
